// @ts-nocheck -- Legacy JavaScript compatibility boundary; runtime behavior intentionally preserved.
import { useState, useEffect } from "react";
import PropTypes from "prop-types";
import useTranslation from "next-translate/useTranslation";
import {
  useGridApiContext,
  useGridSelector,
  gridPageCountSelector,
  gridPageSelector,
  gridPageSizeSelector,
  gridRowCountSelector,
} from "@mui/x-data-grid";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import Tooltip from "@mui/material/Tooltip";
import { Pagination, PaginationItem, TablePagination, useMediaQuery } from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { useTheme } from "@mui/material/styles";
import { Button } from "../button";

export function DefaultPagination({ rowsPerPageOptions, onPageChange, onPageSizeChange }) {
  const { t } = useTranslation("common");
  const theme = useTheme();
  const apiRef = useGridApiContext();
  const page = useGridSelector(apiRef, gridPageSelector);
  const pageCount = useGridSelector(apiRef, gridPageCountSelector);
  const pageSize = useGridSelector(apiRef, gridPageSizeSelector);
  const rowsCount = useGridSelector(apiRef, gridRowCountSelector);

  const [activePage, setActivePage] = useState(page + 1);

  const keyPress = (event) => {
    if (event.keyCode === 13 && event.target.value > 0) {
      onPageChange(Number(event.target.value) - 1);
    }
  };

  const handleInputChange = (event) => {
    let newValue = event.target.value;

    if (newValue !== "" && newValue < 1) {
      newValue = 1;
    }

    if (newValue > pageCount) {
      newValue = pageCount;
    }

    setActivePage(newValue);
  };

  useEffect(() => {
    if (page + 1 !== activePage) {
      setActivePage(page + 1);
    }
  }, [page]);

  return (
    <Box sx={theme.dataGrid.default.box}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 1,
          mr: 2,
        }}
      >
        <Tooltip title={t("dataTablePaginationTooltip")}>
          <Typography variant="body2">{t("page")}:</Typography>
        </Tooltip>
        <TextField
          name="name"
          type="number"
          autoComplete="off"
          onKeyDown={keyPress}
          onChange={handleInputChange}
          value={activePage}
          inputProps={{ max: pageCount, min: 1 }}
          sx={{ width: "5em", input: { p: "4px 3px 5px 10px" } }}
        />
        <Typography variant="body2">/</Typography>
        <Typography variant="body2">{pageCount}</Typography>
      </Box>
      <TablePagination
        component="div"
        page={page}
        count={rowsCount}
        rowsPerPage={pageSize}
        onRowsPerPageChange={(event) => onPageSizeChange(event.target.value)}
        rowsPerPageOptions={rowsPerPageOptions}
        onPageChange={(event, _page) => {
          onPageChange(_page);
          setActivePage(_page + 1);
        }}
        labelDisplayedRows={({ from, to, count }) => `${from}-${to} / ${count}`}
        labelRowsPerPage={t("rowsPerPage")}
      />
    </Box>
  );
}

export function UPTPagination({ onPageChange }) {
  const { t } = useTranslation("common");
  const theme = useTheme();
  const apiRef = useGridApiContext();
  const page = useGridSelector(apiRef, gridPageSelector);
  const pageCount = useGridSelector(apiRef, gridPageCountSelector);
  const mobileMediaQuery = theme.breakpoints.down("md");
  const isMobileDevice = useMediaQuery(mobileMediaQuery);

  const [activePage, setActivePage] = useState(page + 1);

  const handleNextPage = () => {
    const nextPage = page + 1;

    if (nextPage < pageCount) {
      onPageChange(nextPage);
    }
  };

  const handlePrevPage = () => {
    const prevPage = page - 1;

    if (prevPage >= 0) {
      onPageChange(prevPage);
    }
  };

  useEffect(() => {
    if (page + 1 !== activePage) {
      setActivePage(page + 1);
    }
  }, [page]);

  return (
    <Box sx={theme.dataGrid.upt.box}>
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", width: "100%" }}>
        <Pagination
          color="text"
          shape="rounded"
          page={page + 1}
          count={pageCount}
          onChange={(event, _page) => {
            onPageChange(_page - 1);
            setActivePage(_page + 1);
          }}
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            color: theme.palette.primary.main,
            mb: 1,
          }}
          renderItem={(item) => {
            if (item.type === "previous") {
              return (
                <Button
                  {...item}
                  variant="text"
                  color="secondary"
                  sx={{ fontWeight: "700", [mobileMediaQuery]: { display: "none" } }}
                >
                  <ArrowBackIosNewIcon sx={{ m: 1 }} />
                  {t("prevButtonPagination")}
                </Button>
              );
            }

            if (item.type === "next") {
              return (
                <Button
                  {...item}
                  variant="text"
                  color="secondary"
                  sx={{ fontWeight: "700", [mobileMediaQuery]: { display: "none" } }}
                >
                  {t("nextButtonPagination")}
                  <ArrowForwardIosIcon sx={{ m: 1 }} />
                </Button>
              );
            }

            return (
              <PaginationItem
                components={{
                  last: (props) => (
                    <Button {...props} variant="text" color="secondary">
                      {t("lastButton")}
                    </Button>
                  ),
                  next: (props) => (
                    <Button {...props} variant="text" color="secondary" sx={{ fontWeight: "700" }}>
                      {t("nextButtonPagination")}
                      <ArrowForwardIosIcon sx={{ m: 1 }} />
                    </Button>
                  ),
                  first: (props) => (
                    <Button {...props} variant="text" color="secondary">
                      {t("firstButton")}
                    </Button>
                  ),
                  previous: (props) => (
                    <Button {...props} variant="text" color="secondary" sx={{ fontWeight: "700" }}>
                      <ArrowBackIosNewIcon sx={{ m: 1 }} />
                      {t("prevButtonPagination")}
                    </Button>
                  ),
                }}
                {...item}
              />
            );
          }}
        />
        {isMobileDevice ? (
          <Box
            sx={{ display: "flex", width: "100%", justifyContent: "space-between" }}
            onClick={handlePrevPage}
          >
            <Button variant="text" color="secondary" sx={{ fontWeight: "700" }}>
              <ArrowBackIosNewIcon sx={{ m: 1 }} />
              {t("prevButtonPagination")}
            </Button>

            <Button
              variant="text"
              color="secondary"
              sx={{ fontWeight: "700" }}
              onClick={handleNextPage}
            >
              {t("nextButtonPagination")}
              <ArrowForwardIosIcon sx={{ m: 1 }} />
            </Button>
          </Box>
        ) : null}
      </Box>
    </Box>
  );
}

DefaultPagination.propTypes = {
  rowsPerPageOptions: PropTypes.arrayOf(PropTypes.number),
  onPageChange: PropTypes.func.isRequired,
  onPageSizeChange: PropTypes.func.isRequired,
};

UPTPagination.propTypes = {
  onPageChange: PropTypes.func.isRequired,
};
